import React, { FC, useEffect, useState } from "react";
import { Box, Button, Card, CardMedia, Typography } from "@mui/material";
import Breadcrumbs from "../../common/Breadcrumbs";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderListItem } from "../../common/orderListItem/OrderListItem";
import OrderDetails from "../../common/orderDetails/OrderDetails";
import {
  buttonContainerStyle,
  buttonStyle,
  buttonStyleSeller,
  cardContainerStyle,
  cardMediaStyle,
  mainContainerStyle,
  makePaymentButtonStyle,
  textContainerStyle,
} from "./OrderSummaryStyles";
import { orderSummaryStages } from "../../constants/orderSummary";
import { OrderDetails as OrderDetailsProps } from "../../dto/orderSummaryTypes";
import { api } from "../../api/api";
import { invoiceEndPoints, orderEndpoints } from "../../constants/endpoints";
import { UpdateOptions } from "../../common/orderDetails/UpdateOptions";
import ConfirmationDialog from "../../common/confirmationDialog/ConfirmationDialog";
import colors from "../../theme/Colours";
import { DatePicker } from "../../common/orderDetails/DatePicker";
import { useRole } from "../../context/RoleContext";
import { dropdownTitle } from "../../common/orderDetails/orderDetailsStyles";
import Toast from "../../common/Toast";
import InfoSymbol from "../../assets/info-symbol.png";
import moment from "moment";
import { sendCustomEvents } from "../../ga";

const OrderSummary = () => {
  const location = useLocation();
  const { role } = useRole();
  const navigate = useNavigate();
  const orderId = location.pathname.split("/").pop();

  const [orderDetails, setOrderDetails] = useState<OrderDetailsProps>();
  const [isOrderCancelled, setIsOrderCancelled] = useState(false); ///ADDED THIS LINE

  const [currentStage, setCurrentStage] = useState(0);
  const [showDownloadInvoiceError, setShowDownloadInvoiceError] =
    useState(false);

  const orgId = localStorage.getItem("orgId");

  const getOrderDetails = async () => {
    try {
      const response = await api.get(`${orderEndpoints.getOrder}/${orderId}`);
      if (response.data) {
        setOrderDetails(response.data.data);
        const stageIndex = orderSummaryStages.findIndex(
          (stage) => stage.title === response.data.data.status
        );
        response?.data?.data?.eta &&
          setSelectedDate(moment(response.data.data.eta));

        setCurrentStage(stageIndex);

        if (response.data.data.status.toUpperCase() === "CANCELLED") {
          setIsOrderCancelled(true);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const isCancellationAllowed = currentStage < 3;

  const isSellerPortal = () => {
    if (role === "seller" || location.pathname.includes("/seller")) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!orderDetails) getOrderDetails();
  }, []);

  const buttonLabel:
    | "Save"
    | "Download Invoice"
    | "Cancel Order"
    | "Create Invoice" = isSellerPortal()
    ? currentStage + 1 > 3
      ? "Create Invoice"
      : "Save"
    : currentStage + 1 > 3
      ? "Download Invoice"
      : "Cancel Order";

  const [showCancelOrder, setShowCancelOrder] = useState(false);

  const cancelOrder = async () => {
    setShowCancelOrder(false);
    try {
      const response = await api.put(`${orderEndpoints.editOrder}/${orderId}`, {
        newStatus: "CANCELLED",
      });

      if (response?.status === 201) {
        if (response.data.data?.order?.orderStatus === "CANCELLED") {
          setIsOrderCancelled(true);
          setToastMessage("Order has been Cancelled");

          setTimeout(() => {
            navigate(
              `/${orgId}${isSellerPortal() ? "/seller/orders" : "/customer/my-orders"}`
            );
          }, 1000);
        }
      }
    } catch (error) {
      console.error(error);
    }
    setShowToast(true);
  };

  const saveOrder = async () => {
    setError(false);
    try {
      const formattedEta = selectedDate?.startOf("day").format("YYYY-MM-DD");
      const response = await api.put(`${orderEndpoints.editOrder}/${orderId}`, {
        newStatus: orderSummaryStages[currentStage].title
          .toLocaleUpperCase()
          .replaceAll(" ", "_"),
        eta: formattedEta,
      });
      if (response.data) {
        setToastMessage(
          "Order status has been successfully updated and saved!"
        );
      } else {
        setError(true);
        setToastMessage(
          "Failed to update order status. Please try again later."
        );
      }
    } catch (error) {
      setError(true);
      setToastMessage("Failed to update order status. Please try again later.");
      console.error(error);
    }
    setShowToast(true);
  };

  const createInvoice = async () => {
    setTimeout(() => {
      navigate(`/${orgId}/seller/invoice-screen/${orderId}`);
      window.scrollTo(0, 0);
    }, 1500);
  };

  const downloadInvoice = async () => {
    try {
      const response = await api.get(
        `${invoiceEndPoints.downloadInvoice}/${orderId}`
      );
      if (response.data.data) {
        const link = document.createElement("a");
        link.href = response.data.data;
        link.download = `invoice-${orderId}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setShowDownloadInvoiceError(true);
      }
    } catch (error) {
      console.error(error);
      setShowDownloadInvoiceError(true); // Show the error modal if download fail
    }
  };

  const getLinks = () => {
    if (isSellerPortal()) {
      return ["/seller/orders", "#"];
    }
    return ["/customer/home", "/customer/my-orders", "#"];
  };

  const mainAction = () => {
    switch (buttonLabel) {
      case "Save":
        saveOrder();
        break;
      case "Download Invoice":
        downloadInvoice();
        break;
      case "Cancel Order":
        setShowCancelOrder(true);

        break;
      case "Create Invoice":
        createInvoice();
        break;
      default:
        console.log("Button clicked");
    }
    console.log("Button clicked");
  };

  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);

  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const handlePayment = () => {
    navigate(`/${orgId}/customer/my-orders/${orderId}/payment`);
  };

  const showMakePayment =
    !isSellerPortal() &&
    orderDetails?.payment?.status === "PENDING" &&
    orderDetails?.status !== "Cancelled" &&
    orderDetails?.payment?.advancePaymentMandatory;

  return (
    <>
      <ConfirmationDialog
        buttonAction={cancelOrder}
        buttonTitle="Yes, I Am Sure"
        show={showCancelOrder}
        setShow={setShowCancelOrder}
        title="Are you sure you want to cancel?"
      />

      <ConfirmationDialog
        buttonAction={() => {
          downloadInvoice();
          setShowDownloadInvoiceError(false);
        }}
        buttonTitle="Retry"
        error={true}
        show={showDownloadInvoiceError}
        setShow={setShowDownloadInvoiceError}
        title="Invoice not found"
      />

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      <Breadcrumbs
        paths={
          isSellerPortal()
            ? ["All Orders", "Edit Order Status"]
            : ["Home", "My Orders", "Order Confirmation"]
        }
        links={getLinks()}
      />
      {orderDetails ? (
        <>
          {showMakePayment && (
            <Card sx={cardContainerStyle}>
              <CardMedia
                component="img"
                alt="green iguana"
                image={InfoSymbol}
                sx={cardMediaStyle}
              />
              <Box sx={textContainerStyle}>
                <Typography data-testid="paymet-text">
                  Please process the payment to confirm your order
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={makePaymentButtonStyle}
                onClick={() => {
                  sendCustomEvents(
                    "Customer",
                    "Clicked make payment button",
                    "Payment button in Order summary page"
                  );
                  handlePayment();
                }}
              >
                Make Payment
              </Button>
            </Card>
          )}
          <Box sx={mainContainerStyle}>
            <Box>
              {orderDetails?.orders?.map((order, index) => (
                <OrderListItem key={index} {...order} />
              ))}
              <OrderDetails {...orderDetails} currentStage={currentStage} />
            </Box>
            {isSellerPortal() && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}
              >
                <UpdateOptions
                  currentStage={currentStage}
                  setCurrentStage={setCurrentStage}
                />
                {isCancellationAllowed ? (
                  <Box sx={buttonContainerStyle}>
                    <ActionButton
                      label="Cancel Order"
                      onClick={() => {
                        sendCustomEvents(
                          "Customer",
                          "Clicked Cancel Order",
                          "Cancel Order in Order summary"
                        );
                        setShowCancelOrder(true);
                      }}
                      isSellerPortal={isSellerPortal()}
                      outlined
                      disabled={isOrderCancelled}
                    />
                    <ActionButton
                      label="Save"
                      onClick={mainAction}
                      isSellerPortal={isSellerPortal()}
                    />
                  </Box>
                ) : (
                  <>
                    <DatePicker
                      selectedDate={selectedDate}
                      setSelectedDate={setSelectedDate}
                    />
                    <Box sx={{ flexDirection: "row" }}>
                      <Typography sx={dropdownTitle} mb={"30px"}>
                        Invoice
                      </Typography>
                      <ActionButton
                        label="Create Invoice"
                        onClick={mainAction}
                        isSellerPortal={isSellerPortal()}
                        outlined
                        sx={{ marginTop: 0 }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}

            {isSellerPortal() && currentStage + 1 > 3 && (
              <ActionButton
                label="Save"
                onClick={() => saveOrder()}
                isSellerPortal={isSellerPortal()}
                sx={{ marginTop: "37px" }}
              />
            )}

            {!isSellerPortal() && (
              <ActionButton
                label={buttonLabel}
                onClick={mainAction}
                isSellerPortal={isSellerPortal()}
                sx={{ marginTop: { xs: "28px", sm: "40px" } }}
                disabled={isOrderCancelled}
              />
            )}
          </Box>
        </>
      ) : (
        <Box sx={[mainContainerStyle, { textAlign: "center" }]}>
          <Typography>{`Order #${orderId} not found`}</Typography>
        </Box>
      )}
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
        data-testid={error ? "toast-error" : "toast-success"}
      />
    </>
  );
};

export default OrderSummary;

type ButtonProps = {
  label: string;
  onClick: () => void;
  outlined?: boolean;
  isSellerPortal: boolean;
  sx?: object;
  disabled?: boolean;
};

export const ActionButton: FC<ButtonProps> = ({
  label,
  onClick,
  outlined = false,
  isSellerPortal,
  sx = {},
  disabled = false,
}) => {
  return (
    <Button
      sx={[
        isSellerPortal ? buttonStyleSeller : buttonStyle,
        outlined && {
          "&:hover": {
            backgroundColor: colors.background2,
          },
        },
        sx,
      ]}
      variant={outlined ? "outlined" : "contained"}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </Button>
  );
};
